import {
  formatEmailText,
  htmlToText,
  getMessageByInternetMessageId,
  getEmailConversationThread,
} from "common";
import { getEmailBody } from "./outlookUtils";
import { describeMailboxItem } from "./outlookLoadDebug";

const bodyResultToText = (result, bodyType) => {
  const raw = result?.value || "";
  if (bodyType === Office.CoercionType.Html) {
    return htmlToText(raw);
  }
  return raw;
};

const tryOfficeBody = async (item, bodyType, caller) => {
  const result = await getEmailBody(item, bodyType, caller, { maxAttempts: 1 });
  return bodyResultToText(result, bodyType);
};

const graphMessageToText = (message) => {
  const raw = message?.body?.content || "";
  if (message?.body?.contentType === "html") {
    return htmlToText(raw);
  }
  return raw;
};

/**
 * Loads email body text for read-mode context loading, with fallbacks when Office.js body.getAsync fails.
 * When host.kind === 'vsto', uses OOM bridge only (no Graph fallback).
 */
export const loadReadModeEmailBodyText = async (itemOrHost, options = {}) => {
  const host = options.host;
  const item = host ? null : itemOrHost;
  const { caller = "fetchDataEmailRead", graphToken, run } = options;

  const log = (phase, extra = {}) => {
    if (typeof run?.log === "function") {
      run.log(phase, extra);
    } else {
      console.log(`[emailBodyLoader] ${phase}`, extra);
    }
  };

  const snapshot = host?.getSnapshot?.() || item;

  const formatDocumentContent = (bodyText) =>
    formatEmailText(
      snapshot?.from?.emailAddress || "",
      snapshot?.to?.[0]?.emailAddress || "",
      snapshot?.subject || "",
      bodyText || ""
    );

  if (host && host.kind === "vsto") {
    try {
      log("trying vsto-body-text", { item: snapshot });
      const bodyText = await host.getBodyText(`${caller}:vsto`);
      log("vsto-body-text OK", { bodyLength: bodyText.length });
      return { documentContent: formatDocumentContent(bodyText), source: "vsto-body-text", bodyText };
    } catch (error) {
      log("vsto-body-text FAIL", { error: error?.message });
      return { documentContent: formatDocumentContent(""), source: "empty", bodyText: "" };
    }
  }

  if (!item) {
    log("using empty body fallback", { reason: "no item" });
    return { documentContent: formatDocumentContent(""), source: "empty", bodyText: "" };
  }

  const officeAttempts = [
    { source: "office-text", bodyType: Office.CoercionType.Text },
    { source: "office-html", bodyType: Office.CoercionType.Html },
  ];

  for (const { source, bodyType } of officeAttempts) {
    try {
      log(`trying ${source}`, { item: describeMailboxItem(item) });
      const bodyText = await tryOfficeBody(item, bodyType, `${caller}:${source}`);
      log(`${source} OK`, { bodyLength: bodyText.length });
      return { documentContent: formatDocumentContent(bodyText), source, bodyText };
    } catch (error) {
      log(`${source} FAIL`, { error: error?.message });
    }
  }

  if (graphToken) {
    if (item.internetMessageId) {
      try {
        log("trying graph-internetMessageId", { messageId: item.internetMessageId });
        const message = await getMessageByInternetMessageId(graphToken, item.internetMessageId);
        const bodyText = graphMessageToText(message);
        log("graph-internetMessageId OK", { bodyLength: bodyText.length });
        return {
          documentContent: formatDocumentContent(bodyText),
          source: "graph-internetMessageId",
          bodyText,
        };
      } catch (error) {
        log("graph-internetMessageId FAIL", { error: error?.message });
      }
    }

    if (item.conversationId) {
      try {
        log("trying graph-conversationId", { conversationId: item.conversationId });
        const { conversation } = await getEmailConversationThread(graphToken, item.conversationId);
        const bodyText = graphMessageToText(conversation?.[0]);
        log("graph-conversationId OK", { bodyLength: bodyText.length });
        return {
          documentContent: formatDocumentContent(bodyText),
          source: "graph-conversationId",
          bodyText,
        };
      } catch (error) {
        log("graph-conversationId FAIL", { error: error?.message });
      }
    }
  } else {
    log("graph fallback skipped", { reason: "no graph token" });
  }

  log("using empty body fallback");
  return { documentContent: formatDocumentContent(""), source: "empty", bodyText: "" };
};
